/*
Publicacion en Chileautos
Autor: Benjamín León
Fecha: 2025-02-15
*/
import { useEffect, useState } from 'react'

import Vehicle from '../interfaces/Vehicle'
import useChaFetcher from '../hooks/useChaFetcher'
import { useAuth } from '../hooks/useAuth'
import useChaPublisher from '../hooks/useChaPublisher'
import { Rule, validateFields } from '../utils/validateForm'
import ChaInputVehicle from '../interfaces/ChaInputVehicle'
import usePublicationDataSaver from '../hooks/usePublicationDataSaver'
import { autoSelectColor } from '../utils/colorMappingHelpers'
import { getCorrectedModelName } from '../utils/modelNameCorrections'

import { SelectField } from './SelectField'
import { ButtonAction } from './Buttons'
import { Info } from './MessageInfo'
import DisplayError from './DisplayError'
import ConfirmationModal from './ConfirmationModal'
import InputField from './InputField'
import InputTextArea from './InputTextArea'

interface ChaData {
  type: string
  body: string
  brand: string
  model: string
  year: number
  fuel: string
  gearbox: string
  comments: string
  color: string
  title: string
}

// Motorcycle body type mapping for ChileAutos
// NOTE: "Trail" is not valid in ChileAutos - use "Todo Terreno" instead
const MOTORCYCLE_BODY_MAP: Record<string, string> = {
  'BODY.MOTORCYCLE.SIMPLE': 'Todo Terreno',
  'BODY.MOTORCYCLE.TRAIL': 'Todo Terreno',
  'BODY.MOTORCYCLE.SREET': 'Sport calle - urbanas',
  'BODY.MOTORCYCLE.SCOOTER': 'Scooter',
  'BODY.MOTORCYCLE.CROSS': 'Enduro - cross',
  'BODY.MOTORCYCLE.SPORT': 'Sport calle - urbanas',
  'BODY.MOTORCYCLE.CUSTOM': 'Custom',
  'BODY.MOTORCYCLE.TRICYCLE': 'Triciclo',
}

const Cha: React.FC<{ vehicle: Vehicle; onRefresh: () => void }> = ({ vehicle, onRefresh }) => {
  const makeTitle = (vehicle: Vehicle) => {
    // Build title with proper whitespace normalization to avoid Chileautos rejection
    const parts = [
      vehicle.year?.toString().trim(),
      vehicle.brand?.trim(),
      vehicle.model?.trim(),
      vehicle.version?.trim(),
    ].filter(Boolean) // Remove empty/undefined values

    return parts.join(' ')
  }

  // Helper function to get body value with motorcycle mapping fallback
  const getBodyValue = (): string => {
    // First check if we have a format value
    if (vehicle.body_format) {
      // If it's "Trail" (which is invalid), map to "Todo Terreno"
      if (vehicle.body_format === 'Trail') {
        return 'Todo Terreno'
      }

      return vehicle.body_format
    }

    // Map motorcycle body enums to Chileautos values
    if (vehicle.body?.startsWith('BODY.MOTORCYCLE.')) {
      return MOTORCYCLE_BODY_MAP[vehicle.body] || ''
    }

    return vehicle.body
  }

  const { userData } = useAuth()
  // Los valores iniciales. Notese que usamos "xxx_format" en los que
  // vienen desde el vehiculo y son "combos" fijos
  const [formData, setFormData] = useState<ChaData>({
    type: vehicle.type_format || vehicle.type,
    body: getBodyValue(),
    brand: vehicle.brand,
    model: getCorrectedModelName(vehicle.brand, vehicle.model), // Apply model name corrections
    year: vehicle.year,
    fuel: vehicle.fuel_format || vehicle.fuel,
    gearbox: vehicle.gearbox_format || vehicle.gearbox,
    // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
    comments: vehicle.promo?.message || ' ',
    color: vehicle.color,
    title: makeTitle(vehicle),
  })
  const [brands, setBrands] = useState<string[]>([])
  const [types, setTypes] = useState<string[]>([])
  const [bodies, setBodies] = useState<string[]>([])

  // Helper to determine vehicle type for API calls
  const getVehicleType = (): string | undefined => {
    const typeLower = formData.type.toLowerCase()

    if (typeLower === 'moto' || typeLower === 'motocicleta') return 'moto'

    return undefined
  }

  const [fuel, setFuel] = useState<string[]>([])
  const [gearbox, setGearbox] = useState<string[]>([])
  const [color, setColor] = useState<string[]>([])

  const {
    chaBrands,
    errorChaBrands,
    chaTypes,
    errorChaTypes,
    chaModels,
    errorChaModels,
    chaBodies,
    errorChaBodies,
    chaFuel,
    errorChaFuel,
    chaGearbox,
    errorChaGearbox,
    chaColor,
    errorChaColor,
  } = useChaFetcher()

  const { chaPublish, errorChaPublish, chaSetPublished, errorChaSetPublished, chaUpdate, errorChaUpdate, loading } =
    useChaPublisher()
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  const [allBodies, setAllBodies] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)

  const [disabled, setDisabled] = useState<boolean>(false)
  const [faltaMsg, setFaltaMsg] = useState<string>('')
  const [saveMessage, setSaveMessage] = useState<string>('')
  // Se deriva del vehículo en cada render en vez de guardarse en estado: el
  // efecto que lo calculaba corre una sola vez (deps []), así que cualquier
  // cambio del vehículo lo dejaba desfasado. Es el mismo problema que hacía que
  // Mercado Libre siguiera ofreciendo "Publicar" sobre un aviso ya publicado.
  // Se mira el identificador porque es lo que decide si la API crea un aviso
  // nuevo o actualiza el que existe.
  const publicado = !!vehicle.external_chileautos?.identifier || !!vehicle.external_chileautos?.publishedAt

  // Edit mode functionality
  const [editMode, setEditMode] = useState<'none' | 'edit'>('none')
  const [originalFormData, setOriginalFormData] = useState<ChaData | null>(null)
  const [editErrors, setEditErrors] = useState<{ [key: string]: string }>({})

  // Track if all data has been loaded to ensure proper originalFormData capture
  const [dataLoadingComplete, setDataLoadingComplete] = useState<boolean>(false)
  const [initialFormDataSnapshot, setInitialFormDataSnapshot] = useState<ChaData | null>(null)
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)

  // Save draft functionality
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    const result = await savePublicationData(vehicle._id, 'chileautos', formData as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Auto-load draft data when page loads
  const autoLoadDraftData = async () => {
    setIsDraftLoading(true)

    const result = await loadPublicationData<ChaData>(vehicle._id, 'chileautos')

    if (result.success && result.data) {
      // Merge draft data with current form data, draft takes priority
      setFormData((prev) => {
        const merged = { ...prev, ...result.data }

        // Apply model name correction to the merged data
        if (merged.brand && merged.model) {
          merged.model = getCorrectedModelName(merged.brand, merged.model)
        }

        return merged
      })
    } else if (!result.success) {
      // Silently fail for auto-load - don't show error messages
      console.warn('Failed to auto-load draft:', result.message)
    }
    // If no draft exists (result.success && !result.data), do nothing - use vehicle defaults

    setIsDraftLoading(false)
  }

  // Capture initial form data before any modifications
  useEffect(() => {
    if (!initialFormDataSnapshot) {
      setInitialFormDataSnapshot({
        type: vehicle.type_format,
        body: vehicle.body_format,
        brand: vehicle.brand,
        model: getCorrectedModelName(vehicle.brand, vehicle.model),
        year: vehicle.year,
        fuel: vehicle.fuel_format,
        gearbox: vehicle.gearbox_format,
        comments: vehicle.comments,
        color: vehicle.color,
        title: makeTitle(vehicle),
      })
    }
  }, [])

  useEffect(() => {
    const fetchBrandsAsync = async () => {
      const vehicleType = getVehicleType()
      const toutBrands = await chaBrands(vehicleType)
      //Eliminamos las que tienen solo un caracter
      // o son solo numericos
      const pantaBrands = toutBrands.filter((b) => b.length > 1 && !Number(b))
      setBrands(pantaBrands)
      // REMOVED: Brand validation that clears field
      // User will select valid values from dropdown
    }
    const fetchTypesAsync = async () => {
      const allTypes = await chaTypes()
      // Los pasamos a un arreglo de strings
      const tout = Object.values(allTypes)
      tout.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      setTypes(tout)
      // REMOVED: Type validation (was already commented out)
    }
    const fetchBodiesAsync = async () => {
      const vehicleType = getVehicleType()
      const allBodies = await chaBodies(vehicleType)
      // Los pasamos a un arreglo de strings
      const toutBodies = Object.values(allBodies)
      toutBodies.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      setAllBodies(toutBodies)
      // REMOVED: Body validation (was already commented out)
    }

    const fetchFuelAsync = async () => {
      const fuel = await chaFuel()
      setFuel(fuel)
      // REMOVED: Fuel validation (was already commented out)
    }

    const fetchGearboxAsync = async () => {
      const data = await chaGearbox()
      setGearbox(data)
    }
    const fetchColorAsync = async () => {
      const data = await chaColor()
      data.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      setColor(data)

      // Auto-select the correct color based on vehicle color for ChileAutos
      if (data && data.length > 0) {
        const colorSelection = autoSelectColor(vehicle.color, data, 'chileautos')

        if (colorSelection.selectedColor) {
          setFormData((prev) => ({ ...prev, color: colorSelection.selectedColor! }))
        }
      }
      // REMOVED: Color validation that clears field
      // User will select valid values from dropdown
    }

    const loadAllData = async () => {
      // Step 1: Load all external APIs
      await Promise.all([
        fetchBrandsAsync(),
        fetchTypesAsync(),
        fetchBodiesAsync(),
        fetchFuelAsync(),
        fetchGearboxAsync(),
        fetchColorAsync(),
      ])

      // Step 2: Mark external data loading as complete
      setDataLoadingComplete(true)
    }

    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      // SEQUENTIAL LOADING: Load dependencies first, then draft data
      // Step 1: Load all external APIs and their data
      await loadAllData()

      // Step 2: Now that APIs are loaded, apply draft data
      await autoLoadDraftData()

      setExternalApisComplete(true)
    }

    initializeComponentData()

    let sw = true

    if (!userData?.organization?.external?.chileautos?.enabled) {
      setFaltaMsg('Su organización no está autoriada para publicar en Chileautos.')
      sw = false
    } else if (!userData?.organization?.external?.chileautos?.sellerId) {
      setFaltaMsg("Falta el 'sellerId' para publicar en Chileautos.")
      sw = false
    }

    if (!sw) {
      setDisabled(true)
    }

    // Colocamos un numero grande para asegurarnos de hacer scroll hasta el final
    window.scrollTo({ top: 10000, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    setBodies(allBodies)
  }, [formData.type, allBodies])

  // Re-fetch brands when type changes (for motorcycle support)
  useEffect(() => {
    const fetchBrandsOnTypeChange = async () => {
      const vehicleType = getVehicleType()
      const toutBrands = await chaBrands(vehicleType)
      const pantaBrands = toutBrands.filter((b) => b.length > 1 && !Number(b))
      setBrands(pantaBrands)
    }
    fetchBrandsOnTypeChange()
  }, [formData.type])

  useEffect(() => {
    const fetchModelsAsync = async () => {
      if (formData.brand) {
        const vehicleType = getVehicleType()
        const models = await chaModels(formData.brand, vehicleType)

        // Apply model name corrections to the fetched models
        // This ensures corrected model names are available in the dropdown
        const correctedModels = models.map((model) => getCorrectedModelName(formData.brand, model))

        setModels(correctedModels)
        // REMOVED: Model validation that clears field
        // User will select valid model from dropdown
      } else {
        setModels([])
      }
    }
    fetchModelsAsync()
  }, [formData.brand])

  // General input change handler for fields without dependencies
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (formData) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }))
    }
  }

  // Cuando se cambia la marca, limpiamos el modelo
  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (formData) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
        model: '', // Clear model when brand changes
      }))
    }
  }

  // Cuando se cambia el tipo
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prevFormData) => {
      const newFormData = {
        ...prevFormData,
        [name]: value,
        body: '',
        brand: '', // Clear brand when type changes
        model: '', // Clear model when type changes
      }

      // Debug type changes specifically
      // console.log(`🔄 TYPE CHANGE EVENT: ${name}`, {
      //     fieldName: name,
      //     fieldType: e.target.tagName,
      //     previousValue: prevFormData[name as keyof ChaData],
      //     newValue: value,
      //     bodyReset: true, // This field also resets body
      //     editMode: editMode,
      //     hasOriginalData: !!originalFormData,
      //     timestamp: new Date().toISOString()
      // });

      return newFormData
    })
  }

  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    // NEW: Save draft data first before publishing
    console.log('Saving draft data before publishing to ChileAutos...')
    const saveResult = await savePublicationData(
      vehicle._id,
      'chileautos',
      formData as unknown as Record<string, unknown>
    )

    if (!saveResult.success) {
      setFaltaMsg(`❌ Error al guardar datos: ${saveResult.message}`)

      return
    }

    console.log('✅ Draft data saved successfully, proceeding with ChileAutos publication')

    // Existing publish logic (unchanged)
    const organization = userData?.organization

    if (!organization) return false

    if (!formData) {
      return false
    }

    if (!vehicle.pictures?.public) {
      setFaltaMsg('Vehículo no tiene imágenes públicas.')

      return false
    }

    const pictures = vehicle.pictures.public

    const chas: ChaInputVehicle = {
      ...vehicle,
      ...formData,
      organization: organization,
      pictures: pictures,
      // Se manda el identificador existente si lo hay: en '' la API lo toma como
      // aviso nuevo, genera otro uuid y duplica el aviso en ChileAutos, dejando
      // el original vivo y sin referencia en Veekls.
      inventoryIdentifier: vehicle.external_chileautos?.identifier ?? '',
      characteristics: vehicle.characteristics || [],
    }

    console.log('Publishing to ChileAutos:', chas)

    const respPublish = await chaPublish(chas)

    if (respPublish?.ok) {
      const respSetPublished = await chaSetPublished(
        vehicle._id,
        userData.profile?._id ?? '',
        respPublish.inventoryIdentifier ?? ''
      )

      if (respSetPublished === 'OK') {
        setFaltaMsg(`✅ Vehículo guardado y publicado exitosamente en ChileAutos`)
        // `publicado` ya se deriva del vehículo: onRefresh lo actualiza.
        onRefresh()
      }
    } else {
      setFaltaMsg('❌ Error al publicar en ChileAutos')
    }
  }

  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  // Edit mode functions
  const validateEditFields = (): boolean => {
    // console.log("🔍 VALIDATION STARTED", {
    //     formDataBeforeValidation: { ...formData },
    //     timestamp: new Date().toISOString()
    // });

    const validationErrors = validateFields([
      { name: 'type', value: formData.type, rule: Rule.REQUIRED },
      { name: 'body', value: formData.body, rule: Rule.REQUIRED },
      { name: 'brand', value: formData.brand, rule: Rule.REQUIRED },
      { name: 'model', value: formData.model, rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox, rule: Rule.REQUIRED },
      { name: 'color', value: formData.color, rule: Rule.REQUIRED },
      { name: 'title', value: formData.title, rule: Rule.REQUIRED },
    ])

    setEditErrors(validationErrors)

    const isValid = Object.keys(validationErrors).length === 0

    // console.log("🔍 VALIDATION COMPLETED", {
    //     formDataAfterValidation: { ...formData },
    //     validationErrors,
    //     isValid,
    //     errorCount: Object.keys(validationErrors).length,
    //     timestamp: new Date().toISOString()
    // });

    return isValid
  }

  const onEditClick = () => {
    setEditMode('edit')

    // Wait for data loading to complete before capturing original state
    const captureOriginalData = () => {
      if (dataLoadingComplete) {
        // FIXED: Use the actual vehicle data as baseline, not potentially modified formData
        const trueOriginal: ChaData = {
          type: vehicle.type_format,
          body: vehicle.body_format,
          brand: vehicle.brand,
          model: getCorrectedModelName(vehicle.brand, vehicle.model),
          year: vehicle.year,
          fuel: vehicle.fuel_format,
          gearbox: vehicle.gearbox_format,
          comments: vehicle.comments,
          color: vehicle.color,
          title: makeTitle(vehicle),
        }

        setOriginalFormData(trueOriginal)

        // console.log("🎯 EDIT MODE: Captured original form data (FIXED)", {
        //     trueOriginalData: trueOriginal,
        //     currentFormData: formData,
        //     dataLoadingComplete,
        //     vehicleColor: vehicle.color,
        //     // Detailed comparison of true original vs current form
        //     baselineComparison: {
        //         type: {
        //             vehicle: vehicle.type_format,
        //             formData: formData.type,
        //             willDetectChange: vehicle.type_format !== formData.type
        //         },
        //         body: {
        //             vehicle: vehicle.body_format,
        //             formData: formData.body,
        //             willDetectChange: vehicle.body_format !== formData.body
        //         },
        //         brand: {
        //             vehicle: vehicle.brand,
        //             formData: formData.brand,
        //             willDetectChange: vehicle.brand !== formData.brand
        //         },
        //         color: {
        //             vehicle: vehicle.color,
        //             formData: formData.color,
        //             willDetectChange: vehicle.color !== formData.color
        //         },
        //         fuel: {
        //             vehicle: vehicle.fuel_format,
        //             formData: formData.fuel,
        //             willDetectChange: vehicle.fuel_format !== formData.fuel
        //         },
        //         gearbox: {
        //             vehicle: vehicle.gearbox_format,
        //             formData: formData.gearbox,
        //             willDetectChange: vehicle.gearbox_format !== formData.gearbox
        //         },
        //         comments: {
        //             vehicle: vehicle.comments,
        //             formData: formData.comments,
        //             willDetectChange: vehicle.comments !== formData.comments
        //         }
        //     }
        // });
      } else {
        // console.log("⏳ WAITING: Data loading not complete, retrying capture in 100ms", {
        //     dataLoadingComplete,
        //     currentFormData: formData
        // });
        // If data is still loading, wait a bit more
        setTimeout(captureOriginalData, 100)
      }
    }

    captureOriginalData()
  }

  const onConfirmEdit = async () => {
    // console.log("🎬 CONFIRMATION PROCESS STARTED", {
    //     timestamp: new Date().toISOString(),
    //     formDataAtStart: { ...formData },
    //     originalFormDataAtStart: originalFormData ? { ...originalFormData } : null,
    //     editMode: editMode
    // });

    if (!validateEditFields()) {
      // console.log("❌ VALIDATION FAILED - Form data after validation:", {
      //     formDataAfterValidation: { ...formData },
      //     originalFormData: originalFormData ? { ...originalFormData } : null,
      //     validationErrors: editErrors
      // });
      return
    }

    // console.log("✅ VALIDATION PASSED - Form data after validation:", {
    //     formDataAfterValidation: { ...formData },
    //     originalFormData: originalFormData ? { ...originalFormData } : null
    // });

    const organization = userData?.organization

    if (!organization) return

    if (!vehicle.pictures?.public) {
      setFaltaMsg('Vehículo no tiene imágenes públicas.')

      return
    }

    const pictures = vehicle.pictures.public
    const identifier = vehicle.external_chileautos?.identifier ?? ''

    // console.log("🔍 BEFORE CHANGE DETECTION - Final form data check:", {
    //     formDataBeforeDetection: { ...formData },
    //     originalFormDataBeforeDetection: originalFormData ? { ...originalFormData } : null,
    //     formDataReference: formData,
    //     originalDataReference: originalFormData
    // });

    const vehicleCha: ChaInputVehicle = {
      ...vehicle,
      ...formData,
      organization: organization,
      pictures: pictures,
      inventoryIdentifier: identifier,
    }

    // Determine changed fields by comparing with original
    const changedFields: string[] = []

    if (originalFormData) {
      Object.keys(formData).forEach((key) => {
        const typedKey = key as keyof ChaData
        const oldValue = originalFormData[typedKey]
        const newValue = formData[typedKey]

        if (oldValue !== newValue) {
          changedFields.push(key)
          // console.log(`🔍 CHANGE DETECTED: ${key}`, {
          //     oldValue,
          //     newValue,
          //     changed: true,
          //     ...(key === 'comments' && {
          //         commentsSpecialDebug: {
          //             oldValueType: typeof oldValue,
          //             newValueType: typeof newValue,
          //             oldValueLength: typeof oldValue === 'string' ? oldValue?.length || 0 : 0,
          //             newValueLength: typeof newValue === 'string' ? newValue?.length || 0 : 0,
          //             oldValueTrimmed: typeof oldValue === 'string' ? oldValue?.trim() : String(oldValue),
          //             newValueTrimmed: typeof newValue === 'string' ? newValue?.trim() : String(newValue),
          //             strictEquals: oldValue === newValue,
          //             trimmedEquals: typeof oldValue === 'string' && typeof newValue === 'string' ? oldValue?.trim() === newValue?.trim() : false
          //         }
          //     })
          // });
        } else {
          // console.log(`📝 NO CHANGE: ${key}`, {
          //     oldValue,
          //     newValue,
          //     changed: false,
          //     ...(key === 'comments' && {
          //         commentsSpecialDebug: {
          //             oldValueType: typeof oldValue,
          //             newValueType: typeof newValue,
          //             oldValueLength: typeof oldValue === 'string' ? oldValue?.length || 0 : 0,
          //             newValueLength: typeof newValue === 'string' ? newValue?.length || 0 : 0,
          //             bothEmpty: !oldValue && !newValue,
          //             bothSameString: oldValue === newValue,
          //             originalVehicleComments: vehicle.comments,
          //             matchesOriginalVehicle: oldValue === vehicle.comments
          //         }
          //     })
          // });
        }
      })

      // console.log("🎯 EDIT CONFIRMATION: Change detection summary", {
      //     originalFormData,
      //     currentFormData: formData,
      //     changedFields,
      //     changedFieldsCount: changedFields.length,
      //     allFields: Object.keys(formData),
      //     // Special focus on comments
      //     commentsAnalysis: {
      //         originalComments: originalFormData.comments,
      //         currentComments: formData.comments,
      //         commentsChanged: originalFormData.comments !== formData.comments,
      //         commentsInChangedFields: changedFields.includes('comments')
      //     }
      // });
    } else {
      // console.warn("⚠️ EDIT ERROR: No original form data available for comparison");
    }

    if (changedFields.length === 0) {
      // console.warn("⚠️ EDIT WARNING: No changes detected, but proceeding with update");
      setInfoMessage('No se detectaron cambios para actualizar')

      return
    }

    // console.log("🚀 SENDING UPDATE: ChileAutos vehicle update", {
    //     changedFields,
    //     identifier,
    //     vehicleId: vehicle._id
    // });

    const respUpdate = await chaUpdate(vehicleCha, changedFields, false)

    if (respUpdate?.ok) {
      setInfoMessage('Publicación actualizada exitosamente')
      setEditMode('none')
      setEditErrors({})
      onRefresh()
    }
  }

  const onCancelEdit = () => {
    setEditMode('none')
    setEditErrors({})

    if (originalFormData) {
      setFormData(originalFormData)
    }
  }

  const publicarStr = publicado ? 'Republicar' : 'Publicar'
  const publicandoStr = publicado ? 'Republicando' : 'Publicando'

  return (
    <div className="mb-4">
      <div className="border">
        <DisplayError msg={faltaMsg} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <InputField
          label="Título"
          name="title"
          value={formData.title}
          type="text"
          handleInputChange={handleInputChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Tipo"
          name="type"
          value={formData.type}
          options={types}
          handleInputChange={handleTypeChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Carrocería"
          name="body"
          value={formData.body}
          options={bodies}
          handleInputChange={handleInputChange}
          errors={editErrors}
        />

        <SelectFromArray
          label="Marca"
          name="brand"
          value={formData.brand}
          options={brands}
          handleInputChange={handleBrandChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Modelo"
          name="model"
          value={formData.model}
          options={models}
          handleInputChange={handleInputChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Combustible"
          name="fuel"
          value={formData.fuel}
          options={fuel}
          handleInputChange={handleInputChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Transmisión"
          name="gearbox"
          value={formData.gearbox}
          options={gearbox}
          handleInputChange={handleInputChange}
          errors={editErrors}
        />
        <SelectFromArray
          label="Color"
          name="color"
          value={formData.color}
          options={color}
          handleInputChange={handleInputChange}
          errors={editErrors}
        />
      </div>

      <InputTextArea
        label="Comentarios"
        name="comments"
        value={formData?.comments}
        handleInputChange={handleInputChange}
      />

      <div>{errorChaTypes?.message}</div>
      <div>{errorChaBodies?.message}</div>
      <div>{errorChaBrands?.message}</div>
      <div>{errorChaModels?.name}</div>
      <div>{errorChaFuel?.message}</div>
      <div>{errorChaGearbox?.message}</div>
      <div>{errorChaColor?.message}</div>
      <DisplayError msg={errorChaPublish?.message} />
      <DisplayError msg={errorChaSetPublished?.message} />
      <DisplayError msg={errorChaUpdate?.message} />
      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
      {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
        !(saveMessage && saveMessage.startsWith('✅')) && (
          <div className="flex justify-center mt-2">
            <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
          </div>
        )}
      {(saveMessage || saveError) && (
        <div className="flex justify-center mt-2">
          <div className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMessage || saveError}
          </div>
        </div>
      )}
      <div className="flex justify-center mt-4 gap-4">
        <ButtonAction
          label={savingLoading ? 'Guardando...' : 'Guardar'}
          handleButtonClick={handleSaveDraft}
          disabled={disabled || loading || savingLoading}
        />

        {!publicado ? (
          // Initial publication button
          <ButtonAction
            label={loading ? `${publicandoStr}...` : `${publicarStr} en Chileautos`}
            handleButtonClick={() => setConfirmationModalOpen(true)}
            disabled={disabled || loading}
          />
        ) : editMode === 'none' ? (
          // Edit button for published vehicles
          <ButtonAction label="Editar Publicación" handleButtonClick={onEditClick} disabled={disabled || loading} />
        ) : editMode === 'edit' ? (
          // Edit confirmation buttons
          <>
            <ButtonAction
              label={loading ? 'Actualizando...' : 'Confirmar Cambios'}
              handleButtonClick={onConfirmEdit}
              disabled={disabled || loading}
            />
            <ButtonAction label="Cancelar" handleButtonClick={onCancelEdit} disabled={loading} />
          </>
        ) : null}
      </div>
      {confirmationModalOpen && !(disabled || loading) && (
        <ConfirmationModal
          message={`¿Estás seguro de ${publicarStr} en Chileautos?`}
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}
    </div>
  )
}

const SelectFromArray: React.FC<{
  label: string
  name: string
  value?: string
  options: string[] | undefined
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
}> = ({ label, name, value, options, handleInputChange, errors }) => {
  // Convert array to record
  const optionsRecord: Record<string, string> = (options ?? []).reduce(
    (acc, option) => {
      acc[option.toString()] = option.toString()

      return acc
    },
    {} as Record<string, string>
  )

  return (
    <SelectField
      label={label}
      name={name}
      value={value?.toString()}
      options={optionsRecord}
      handleInputChange={handleInputChange}
      errors={errors}
    />
  )
}

export default Cha
